import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from './Command';
import { Checkbox } from './Checkbox';
import { cn } from '../../cn';
import { ChevronDown } from 'lucide-react';
import { useResourceKit } from '@resource-kit/core/react';

export interface MultiSelectOption {
  value: string;
  label: string;
}
export interface MultiSelectProps {
  options?: MultiSelectOption[];
  value?: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  className?: string;
  fit?: boolean;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  options = [],
  value = [],
  onChange,
  placeholder,
  className = '',
  fit = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const { t } = useResourceKit();
  const effectivePlaceholder = placeholder || t('rk.multiSelect.select');

  const display = React.useMemo(() => {
    if (!value.length) return effectivePlaceholder;
    if (value.length === 1) return options.find((o) => o.value === value[0])?.label || value[0];
    return t('rk.multiSelect.selected', { count: value.length });
  }, [value, options, effectivePlaceholder, t]);

  const toggle = (val: string) => {
    if (value.includes(val)) onChange(value.filter((v) => v !== val));
    else onChange([...value, val]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <button
            type="button"
            className={cn(
              'flex h-10 items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
              fit ? 'w-auto' : 'w-full',
              className,
            )}
          >
            <span className="truncate text-left">{display}</span>
            <ChevronDown className="h-4 w-4 opacity-50 ml-2" />
          </button>
        }
      />
      <PopoverContent className="w-[200px] p-0" positionerProps={{ align: 'start' }}>
        <Command>
          <CommandInput placeholder={t('rk.ui.search')} />
          <CommandList>
            {options.length === 0 && <CommandEmpty>{t('rk.multiSelect.noOptions')}</CommandEmpty>}
            {options.map((opt) => (
              <CommandItem key={opt.value} onSelect={() => toggle(opt.value)}>
                <div className="flex items-center gap-2 w-full">
                  <Checkbox
                    checked={value.includes(opt.value)}
                    onCheckedChange={() => toggle(opt.value)}
                  />
                  <span>{opt.label}</span>
                </div>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// The default export stays for `../query/QueryBuilder.tsx`, the one
// caller that imports it that way. The NAMED export above is what the package's
// `./react` entry can re-export, because `export *` does not carry a default.
export default MultiSelect;
